import { AddCircleIcon, CoinPlusIcon } from '@synergy/theme-next';
import { ArrayHelpers, Field, FieldArray, FieldProps, FormikProps, getIn } from 'formik';
import { Box, FormHelperText, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Locale, UserRole } from '@synergy/models';
import {
    PALO_QUANTITY_HEAD_SX,
    PaloDirtyCell,
    PaloOverflowAction,
    PaloPositionColumn,
    PaloPositionTable,
    PaloQuantityCell,
    paloHasDescription,
} from '@synergy/components-palo';
import { PoolButton, PoolNumberTextfield, PoolSplitButton } from '@synergy/components-next';
import { ProductDetails } from '@synergy/components';
import { ReactElement, useState } from 'react';
import { getLocalizedString, hasRole } from '@synergy/utils';
import urljoin from 'url-join';

import { InquiryDetailPositionsFormValues } from './inquiry-detail-positions';
import { PaloAddPositionsDialog } from '../../../common/components/palo/palo-add-positions-dialog';
import { PaloPositionText } from '../../../common/components/palo/palo-position-text';
import { PaloRowProductSearch } from '../../../common/components/palo/palo-row-product-search';
import { PositionAttachmentsModal } from '../../../common/components/position-attachments/position-attachments-modal';
import { PurchaseItem } from '../models/purchase-item';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { User } from '../../../common/models/user';
import { setPaloPositionMedia, usePaloPositionMedia } from '../../../common/components/palo/palo-position-media';
import certifiedIcon from '../../../common/style/icons/certificate.svg';

/** The FieldArray helper as Formik hands it over, with the form attached. */
type PurchaseItemsArrayHelper = ArrayHelpers & { form: FormikProps<InquiryDetailPositionsFormValues> };

export interface InquiryDetailPositionsArticlesProps {
    readonly?: boolean;
    accountPurchaseIndex: number;
    user: User;
    locale: Locale;
    mediaBaseUrl: string;
    onOpenCustomPosition: (helper: ArrayHelpers, index?: number, purchaseItem?: PurchaseItem) => void;
}

/** A row that carries no article and no name of its own waits for one. */
const isBlank = (item: PurchaseItem): boolean =>
    !item.product && 0 === Object.values(item.name ?? {}).filter(Boolean).length;

/**
 * The articles asked for from one supplier, in the table every document shares.
 *
 * It had a table of its own with arrows for the order of the rows, a search
 * line at its foot and four icon buttons per row. Carrying a row is a drag
 * now, as everywhere else, and what a row offers stands in its menu (#2208).
 */
export const InquiryDetailPositionsArticles = ({
    readonly = false,
    accountPurchaseIndex,
    user,
    locale,
    mediaBaseUrl,
    onOpenCustomPosition,
}: InquiryDetailPositionsArticlesProps): ReactElement => {
    const [attachmentsFor, setAttachmentsFor] = useState<{ index: number; purchaseItem: PurchaseItem } | undefined>(
        undefined
    );
    const [showAddMultiple, setShowAddMultiple] = useState(false);
    const mayFollowLink = hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], user);
    const fieldName = `accountPurchases.${accountPurchaseIndex}.purchaseItems`;
    const positionMedia = usePaloPositionMedia();

    const itemsOf = (helper: PurchaseItemsArrayHelper): PurchaseItem[] => [
        ...(helper.form.values.accountPurchases[accountPurchaseIndex]?.purchaseItems ?? []),
    ];

    /**
     * Writes the new order in one go. Only the order of the array matters: the
     * backend takes the place in the submitted list as the position.
     */
    const writeItems = (helper: PurchaseItemsArrayHelper, items: PurchaseItem[]): void => {
        helper.form.setFieldValue(fieldName, items);
    };

    const buildColumns = (helper: PurchaseItemsArrayHelper): PaloPositionColumn<PurchaseItem>[] => [
        {
            key: 'article',
            type: 'name',
            label: <FormattedMessage id="app.table.column.article" />,
            render: (item, index) =>
                isBlank(item) && !readonly ? (
                    <PaloRowProductSearch
                        onSelected={product => {
                            const items = itemsOf(helper);

                            items[index] = PurchaseItem.fromPlainObject(PurchaseItem.fromProduct(product, locale));
                            writeItems(helper, items);
                        }}
                        includeNormArticles={true}
                    />
                ) : (
                    <Box
                        sx={
                            item.isBestPrice
                                ? {
                                      background: `url(${certifiedIcon}) no-repeat left`,
                                      display: 'flex',
                                      // The description stands under the name, not beside it.
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                  }
                                : undefined
                        }
                    >
                        <ProductDetails
                            product={item}
                            showDescription={false}
                            inlineNumber={true}
                            url={
                                mayFollowLink && item.product
                                    ? urljoin(ROUTES.products.product.base, item.product.id.toString())
                                    : undefined
                            }
                        />
                        <PaloPositionText
                            index={index}
                            shortDescription={item.shortDescription}
                            longDescription={item.longDescription}
                            onEdit={
                                // Only a free or a norm article has a dialog of its own.
                                !readonly && (item.isNormArticle || !item.product)
                                    ? () => onOpenCustomPosition(helper, index, item)
                                    : undefined
                            }
                        />
                    </Box>
                ),
        },
        {
            key: 'quantity',
            type: 'quantity',
            label: <FormattedMessage id="app.table.column.quantity" />,
            headSx: PALO_QUANTITY_HEAD_SX,
            render: (item, index) => (
                <PaloQuantityCell unit={item.orderUnit}>
                    <PaloDirtyCell name={`${fieldName}.${index}.quantity`}>
                        <Field name={`${fieldName}.${index}.quantity`}>
                            {({ field, meta }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    {...field}
                                    onChange={event =>
                                        helper.replace(
                                            index,
                                            PurchaseItem.fromPlainObject({ ...item, quantity: event.target.value })
                                        )
                                    }
                                    type="dense"
                                    readonly={readonly}
                                    textAlign="right"
                                    fullWidth={true}
                                    error={!!meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                />
                            )}
                        </Field>
                    </PaloDirtyCell>
                </PaloQuantityCell>
            ),
        },
    ];

    const rowActions =
        (helper: PurchaseItemsArrayHelper) =>
        (item: PurchaseItem, index: number): PaloOverflowAction[] =>
            [
                // Drawings, photos and datasheets that belong to this position
                // (key-user brief A3.3). Only a saved position can carry them.
                ...(item.id
                    ? [
                          {
                              label: (
                                  <FormattedMessage
                                      id="app.positions.attachments.tooltip"
                                      values={{ count: positionMedia(item.id, item.media).length }}
                                  />
                              ),
                              onClick: () => setAttachmentsFor({ index, purchaseItem: item }),
                          },
                      ]
                    : []),
                ...(readonly
                    ? []
                    : [
                          ...(item.isNormArticle || !item.product
                              ? [
                                    {
                                        label: <FormattedMessage id="app.common.edit" />,
                                        onClick: () => onOpenCustomPosition(helper, index, item),
                                    },
                                    {
                                        label: <FormattedMessage id="app.common.duplicate" />,
                                        onClick: () => {
                                            const items = itemsOf(helper);

                                            items.splice(
                                                index + 1,
                                                0,
                                                PurchaseItem.fromPlainObject({ ...item, id: undefined })
                                            );
                                            writeItems(helper, items);
                                        },
                                    },
                                ]
                              : []),
                          {
                              label: <FormattedMessage id="app.common.delete" />,
                              danger: true,
                              onClick: () => {
                                  const items = itemsOf(helper);

                                  items.splice(index, 1);
                                  writeItems(helper, items);
                              },
                          },
                      ]),
            ];

    return (
        <FieldArray name={fieldName}>
            {helper => {
                const typed = helper as PurchaseItemsArrayHelper;
                const items = typed.form.values.accountPurchases[accountPurchaseIndex]?.purchaseItems ?? [];
                // The card cannot be saved without a complete position: the
                // blank first row turns red and says why (#2308).
                const listError = getIn(typed.form.errors, fieldName);
                const needsPosition = typed.form.submitCount > 0 && 'string' === typeof listError;

                return (
                    <>
                        <PaloPositionTable
                            items={items}
                            columns={buildColumns(typed)}
                            getRowKey={(item, index) => `${item.id ?? 'new'}-${index}`}
                            hasDescription={item =>
                                paloHasDescription(
                                    value => getLocalizedString([locale], value) ?? '',
                                    item.shortDescription,
                                    item.longDescription
                                )
                            }
                            readonly={readonly}
                            isRowFaulty={item => needsPosition && isBlank(item)}
                            rowActions={rowActions(typed)}
                            quantityOf={item => Number(item.quantity)}
                            onMove={(from, to) => {
                                const next = itemsOf(typed);
                                const [moved] = next.splice(from, 1);

                                next.splice(to, 0, moved);
                                writeItems(typed, next);
                            }}
                            onDeleteMany={indices => {
                                // From the back, so the places of the ones still
                                // to go do not shift under the loop.
                                const next = itemsOf(typed);

                                [...indices].sort((left, right) => right - left).forEach(i => next.splice(i, 1));
                                writeItems(typed, next);
                            }}
                            onDuplicateMany={indices => {
                                const next = itemsOf(typed);

                                [...indices]
                                    .sort((left, right) => right - left)
                                    .forEach(index =>
                                        next.splice(
                                            index + 1,
                                            0,
                                            PurchaseItem.fromPlainObject({ ...next[index], id: undefined })
                                        )
                                    );
                                writeItems(typed, next);
                            }}
                            toolbar={
                                !readonly && (
                                    <Stack direction="row" gap={2} alignItems="flex-end">
                                        <PoolSplitButton
                                            variant="outlined"
                                            color="inherit"
                                            preSelectedIndex={0}
                                            menuItems={[
                                                {
                                                    label: <FormattedMessage id="app.positions.addRow" />,
                                                    displayLabel: <FormattedMessage id="app.positions.addRow" />,
                                                    icon: <AddCircleIcon />,
                                                    onClick: () =>
                                                        helper.push(
                                                            PurchaseItem.fromPlainObject({
                                                                name: {},
                                                                orderUnit: '',
                                                                quantity: 1,
                                                            })
                                                        ),
                                                },
                                                {
                                                    label: <FormattedMessage id="app.freePosition.addFreePosition" />,
                                                    displayLabel: (
                                                        <FormattedMessage id="app.freePosition.addFreePosition" />
                                                    ),
                                                    icon: <CoinPlusIcon />,
                                                    onClick: () => onOpenCustomPosition(helper),
                                                },
                                            ]}
                                            sx={{ flexShrink: 0 }}
                                        />
                                        <PoolButton
                                            variant="outlined"
                                            color="inherit"
                                            startIcon={<AddCircleIcon />}
                                            onClick={() => setShowAddMultiple(true)}
                                        >
                                            <FormattedMessage id="app.positions.addMultiple" />
                                        </PoolButton>
                                        <PaloAddPositionsDialog
                                            open={showAddMultiple}
                                            onClose={() => setShowAddMultiple(false)}
                                            onSelect={product =>
                                                product &&
                                                helper.push(
                                                    PurchaseItem.fromPlainObject(
                                                        PurchaseItem.fromProduct(product, locale)
                                                    )
                                                )
                                            }
                                            includeNormArticles={true}
                                        />
                                    </Stack>
                                )
                            }
                        />

                        {needsPosition && (
                            <FormHelperText error={true}>
                                <FormattedMessage id={listError} />
                            </FormHelperText>
                        )}

                        {attachmentsFor?.purchaseItem.id && (
                            <PositionAttachmentsModal
                                kind="purchase-items"
                                positionId={attachmentsFor.purchaseItem.id}
                                positionName={getLocalizedString([locale], attachmentsFor.purchaseItem.name)}
                                media={positionMedia(attachmentsFor.purchaseItem.id, attachmentsFor.purchaseItem.media)}
                                mediaBaseUrl={mediaBaseUrl}
                                readonly={readonly}
                                onClose={() => setAttachmentsFor(undefined)}
                                // Saved by the dialog itself: no input of the form (R-198).
                                onChange={media =>
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    setPaloPositionMedia(attachmentsFor.purchaseItem.id!, media)
                                }
                            />
                        )}
                    </>
                );
            }}
        </FieldArray>
    );
};
